import React, { useState } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import AuthCard from "../components/AuthCard";
import Input from "../components/Input";
import Button from "../components/Button";
import Divider from "../components/Divider";
import { useAuth } from "../context/AuthContext";

/* ── Inline Google "G" icon ── */
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.0 24.0 0 0 0 0 21.56l7.98-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

/** Friendly error messages for Firebase auth error codes */
function friendlyError(code) {
  const map = {
    "auth/invalid-email": "Invalid email address.",
    "auth/user-disabled": "This account has been disabled.",
    "auth/user-not-found": "No account found with this email.",
    "auth/wrong-password": "Incorrect password.",
    "auth/invalid-credential": "Invalid email or password.",
    "auth/operation-not-allowed": "Email and password sign-in is not enabled yet.",
    "auth/network-request-failed": "Unable to reach Firebase. Check your connection and try again.",
    "auth/too-many-requests": "Too many attempts. Please try again later.",
  };
  return map[code] || "Something went wrong. Please try again.";
}

export default function Login() {
  const { user, login, loginWithGoogle, resetPassword } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const requestedLocation = location.state?.from;
  const returnPath = requestedLocation?.pathname
    ? `${requestedLocation.pathname}${requestedLocation.search || ""}${requestedLocation.hash || ""}`
    : "/home";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState("");

  // If already logged in, continue to the originally requested page.
  if (user) return <Navigate to={returnPath} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResetMessage("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(returnPath, { replace: true });
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const resetEmail = email.trim();
    setError("");
    setResetMessage("");

    if (!resetEmail) {
      setError("Enter your email address first, then choose Forgot Password.");
      return;
    }

    setResetLoading(true);
    try {
      await resetPassword(resetEmail);
      setResetMessage("If an account uses that email, a password reset link is on its way. Check your inbox and spam folder.");
    } catch (err) {
      if (err.code === "auth/invalid-email") {
        setError("Enter a valid email address.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many reset attempts. Please wait a few minutes and try again.");
      } else if (err.code === "auth/user-not-found") {
        setResetMessage("If an account uses that email, a password reset link is on its way. Check your inbox and spam folder.");
      } else {
        setError("We could not send a reset email right now. Please try again.");
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    try {
      await loginWithGoogle();
      navigate(returnPath, { replace: true });
    } catch (err) {
      setError(friendlyError(err.code));
    }
  };

  return (
    <AuthCard
      heading="Sign In"
      subtitle="Return to your dashboards, Progress, tasks, and deadlines"
      footer={
        <p style={{ fontSize: 14, color: "#828282", margin: 0 }}>
          No account?{" "}
          <Link to="/signup" state={location.state} style={{ color: "#1a1a1a", fontWeight: 600, textDecoration: "underline" }}>
            Sign up
          </Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit}>
        {/* ── Error banner ── */}
        {error && (
          <div style={styles.errorBanner} role="alert">{error}</div>
        )}
        {resetMessage && (
          <div style={styles.successBanner} role="status" aria-live="polite">{resetMessage}</div>
        )}

        <Input
          label="Email"
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />

        <Input
          label="Password"
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />

        {/* ── Password reset ── */}
        <div style={styles.row}>
          <button
            type="button"
            style={styles.forgot}
            onClick={handleForgotPassword}
            disabled={resetLoading || loading}
          >
            {resetLoading ? "Sending..." : "Forgot Password?"}
          </button>
        </div>

        <Button variant="primary" type="submit" loading={loading}>
          Sign In
        </Button>
      </form>

      <Divider />

      <Button variant="secondary" icon={<GoogleIcon />} onClick={handleGoogle}>
        Continue with Google
      </Button>
    </AuthCard>
  );
}

const styles = {
  errorBanner: {
    backgroundColor: "#fef2f2",
    color: "#b91c1c",
    border: "1px solid #fecaca",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 13,
    marginBottom: 16,
    textAlign: "center",
  },
  successBanner: {
    backgroundColor: "#f0fdf4",
    color: "#166534",
    border: "1px solid #bbf7d0",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 13,
    lineHeight: 1.45,
    marginBottom: 16,
    textAlign: "center",
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginBottom: 24,
    marginTop: -4,
  },
  forgot: {
    padding: 0,
    border: 0,
    background: "transparent",
    fontFamily: "inherit",
    fontSize: 14,
    color: "#828282",
    textDecoration: "none",
    cursor: "pointer",
  },
};
